/**
 * Pantalla de login — solo Google Workspace SSO.
 *
 * El form clásico de usuario/password fue retirado: la app valida identidad
 * únicamente vía Google. El backend ya soporta `POST /api/auth/google`
 * (verifica el idToken con google-auth-library y busca el user por email
 * en `users`).
 *
 * Si ya hay sesión activa, redirige a `/` (la ruta protegida resuelve a /dashboard).
 */

import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import { MoleculesBackground } from './MoleculesBackground';
import { GoogleSignInButton } from './GoogleSignInButton';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? '/';

  const usuario = useSessionStore((s) => s.usuario);
  const loginConGoogle = useSessionStore((s) => s.loginConGoogle);
  const loading = useSessionStore((s) => s.loading);
  const error = useSessionStore((s) => s.error);
  const addAuditEntry = useAuditStore((s) => s.addEntry);

  // Si ya hay sesión, redirigir.
  useEffect(() => {
    if (usuario) {
      void navigate(from, { replace: true });
    }
  }, [usuario, from, navigate]);

  if (usuario) {
    return <Navigate to={from} replace />;
  }

  // Login con Google Workspace. Recibe el idToken del GIS flow.
  const handleGoogleIdToken = async (idToken: string) => {
    const ok = await loginConGoogle(idToken);
    if (ok) {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'login_google',
        razon: 'Ingreso vía Google Workspace SSO',
      });
      void navigate(from, { replace: true });
    } else {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'login_google_fallido',
        razon: 'Google SSO rechazado (dominio/usuario no registrado)',
      });
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-everex-azul to-everex-naranja p-4">
      {/* Fondo animado (moléculas + hexágonos flotando). Decorativo, aria-hidden. */}
      <MoleculesBackground />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          {/* Logo oficial Everex (descargado del sitio everexpharma.com) */}
          <img
            src="/logo-everex.svg"
            alt="Everex Pharmaceuticals"
            className="mx-auto mb-4 h-12"
          />
          <p className="text-sm text-gray-600">Sistema de Trazabilidad · Riga Traza</p>
        </div>

        <GoogleSignInButton
          onIdToken={(idToken) => void handleGoogleIdToken(idToken)}
          disabled={loading}
        />

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        <p className="mt-6 text-center text-[11px] text-gray-500">
          El acceso se valida con tu cuenta de Google Workspace de la organización.
        </p>
      </div>
    </main>
  );
}
